import { useTranslator } from '@Localization';
import { Button } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { S_Modal } from '../../design/modals/modal/S_Modal';

interface DecryptionScriptSampleModalProps {
  showDecryptionScriptSampleModal: boolean;
  onDecryptionScriptSampleModalCancel: () => void;
}

const sampleScript = `#!/bin/bash
set -e
if [ "$#" -ne 2 ]; then
  echo Usage: $0 archive.zip decryption.key
  exit 1
fi
ARCHIVE_NAME=$1
KEY_NAME=$2
UNZIP_DIRECTORY="$ARCHIVE_NAME encrypted"
DESTINATION_DIRECTORY="$ARCHIVE_NAME decrypted"
unzip "$ARCHIVE_NAME" -d "$UNZIP_DIRECTORY"
mkdir "$DESTINATION_DIRECTORY"
cd "$UNZIP_DIRECTORY"
for FILE in *; do
  tail -c+17 "$FILE" | openssl aes-256-cbc -d -K $(cat "../$KEY_NAME") -iv $(head -c 16 "$FILE" | xxd -ps) -out "../$DESTINATION_DIRECTORY/$FILE"
done`;

export const DecryptionScriptSampleModal = observer((props: DecryptionScriptSampleModalProps): ReactElement => {
  const { t } = useTranslator();

  const copyToClipboard = async (): Promise<void> => {
    const el = document.createElement('textarea');
    el.value = sampleScript;
    document.body.appendChild(el);
    el.select();
    await navigator.clipboard.writeText(el.value);
    document.body.removeChild(el);
  };

  return (
    <S_Modal
      isOpen={props.showDecryptionScriptSampleModal}
      toggle={props.onDecryptionScriptSampleModalCancel}
      headerText={t('decrypt_files_sample_script_header')}
      buttons={
        <>
          <Button
            color='neutral'
            variant='outlined'
            onClick={props.onDecryptionScriptSampleModalCancel}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={() => {
              void copyToClipboard();
            }}
          >
            {t('copy')}
          </Button>
        </>
      }
    >
      <div>{t('downloaded_files_are_encrypted')}</div>
      <SyntaxHighlighter language='bash'>{sampleScript}</SyntaxHighlighter>
    </S_Modal>
  );
});
